import React, { useState } from "react";
import CrossAttachmentAssessment from "./CrossAttachmentAssessment";
import { IP } from "../../internalServer";
import ZoomableImage from "../zoomablePic";
import { iconData } from "../../iconData";
import PdfViewer from "./PdfViewer";
import { Box, Paper, Typography, Chip } from "@mui/material";
import { CheckCircle2 } from "lucide-react";

const ACCENT = "#f77f00";
const ACCENT_SOFT = "#fff4e5";
const BORDER = "#e6e9ef";
const TEXT_PRIMARY = "#1f2733";
const TEXT_SECONDARY = "#5b6472";
const BG_SOFT = "#f8f9fb";
const CORRECT_BG = "#e8f7ee";
const CORRECT_BORDER = "#22c55e";

const PreMcq = ({ question, nextQuestion }) => {
  const [zoomedImage, setZoomedImage] = useState(null);
  const [mideaviewer, setMideaviewer] = useState(false);
  const [responseAttach, setResponseAttach] = useState("");

  const handleClickforpdf = (fileUrl) => {
    setResponseAttach(fileUrl);
    setMideaviewer(true);
  };

  // 🔹 Build set of correct option keys for quick lookup
  const correctOptionKeys = new Set(
    (question?.correct_answers || [])
      .map((ans) => ans?.option_key)
      .filter(Boolean),
  );

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2,
        borderRadius: "14px",
        border: `1px solid ${BORDER}`,
        background: "#fff",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: ACCENT,
          boxShadow: "0 4px 16px rgba(247,127,0,0.08)",
        },
      }}
    >
      {/* Header row: Q number + marks */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Q badge */}
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: `linear-gradient(135deg, ${ACCENT} 0%, #d35400 100%)`,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "13px",
              fontWeight: 700,
              flexShrink: 0,
              boxShadow: "0 3px 8px rgba(247,127,0,0.3)",
            }}
          >
            Q{nextQuestion + 1}
          </Box>

          {/* Title */}
          <Typography
            sx={{
              fontSize: "14.5px",
              fontWeight: 600,
              color: TEXT_PRIMARY,
              lineHeight: 1.5,
              wordBreak: "break-word",
            }}
          >
            {question?.title?.split("\n").map((line, index) => (
              <React.Fragment key={index}>
                {index !== 0 && <br />}
                <span>{line}</span>
              </React.Fragment>
            ))}
          </Typography>
        </Box>

        {/* Marks & Mandatory chips */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {question?.correct_score ? (
            <Chip
              label={`${question.correct_score} Marks`}
              size="small"
              sx={{
                background: ACCENT_SOFT,
                color: ACCENT,
                fontWeight: 700,
                fontSize: "11.5px",
                border: `1px solid ${ACCENT}`,
              }}
            />
          ) : null}
          {question?.is_mandatory && (
            <Chip
              label="* Mandatory"
              size="small"
              sx={{
                background: "#fff0f0",
                color: "#dc3545",
                fontWeight: 600,
                fontSize: "10.5px",
                border: "1px solid #f5c2c7",
              }}
            />
          )}
        </Box>
      </Box>

      {/* Attachments */}
      {question?.attachments?.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          {question?.attachments?.map((attachment, index) => {
            const fileName = attachment || "";
            const fileExtension = fileName.split(".").pop()?.toLowerCase();
            const isImage = ["jpg", "jpeg", "png"].includes(fileExtension);

            return (
              <Box key={index} sx={{ maxWidth: 400 }}>
                {isImage ? (
                  <ZoomableImage
                    src={attachment}
                    alt={`Attachment ${index + 1}`}
                  />
                ) : (
                  <CrossAttachmentAssessment attachment={attachment} />
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Options */}
      {question?.options?.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {question.options.map((option, index) => {
            const isCorrect = correctOptionKeys.has(option?.key);

            return (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 1.25,
                  px: 1.5,
                  borderRadius: "10px",
                  border: `1.5px solid ${isCorrect ? CORRECT_BORDER : BORDER}`,
                  background: isCorrect ? CORRECT_BG : BG_SOFT,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  transition: "all 0.15s ease",
                }}
              >
                {/* Option Key Badge */}
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    background: isCorrect ? CORRECT_BORDER : "#fff",
                    border: `1.5px solid ${
                      isCorrect ? CORRECT_BORDER : BORDER
                    }`,
                    color: isCorrect ? "#fff" : TEXT_SECONDARY,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12.5px",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {option?.key}
                </Box>

                {/* Option Value */}
                <Typography
                  sx={{
                    fontSize: "13.5px",
                    color: TEXT_PRIMARY,
                    fontWeight: isCorrect ? 600 : 400,
                    flex: 1,
                    wordBreak: "break-word",
                  }}
                >
                  {option?.value}
                </Typography>

                {/* Correct Badge */}
                {isCorrect && (
                  <Chip
                    icon={<CheckCircle2 size={12} color="#fff" />}
                    label="Correct"
                    size="small"
                    sx={{
                      background: CORRECT_BORDER,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: "10.5px",
                      height: 22,
                      "& .MuiChip-icon": { ml: "6px", color: "#fff" },
                    }}
                  />
                )}

                {/* Option Attachment */}
                {option.attachment &&
                  (() => {
                    const fileUrl =
                      typeof option.attachment === "string"
                        ? option.attachment
                        : option.attachment?.url;

                    if (!fileUrl) return null;

                    const fileType = fileUrl.split(".").pop().toLowerCase();
                    const isImg = ["jpg", "jpeg", "png"].includes(fileType);
                    const isVideo = ["mp4", "mov"].includes(fileType);
                    const isPdf = fileType === "pdf";

                    const fileIcon =
                      iconData?.find((i) => i.extension === fileType)?.icon ||
                      "/images/file_icon.png";

                    const fullUrl = fileUrl.startsWith("http")
                      ? fileUrl
                      : `${IP}${fileUrl}`;

                    return (
                      <Box
                        sx={{
                          ml: "auto",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {isImg ? (
                          <img
                            src={fullUrl}
                            alt="attachment"
                            onClick={() => setZoomedImage(fullUrl)}
                            style={{
                              width: 42,
                              height: 42,
                              objectFit: "cover",
                              borderRadius: "6px",
                              cursor: "pointer",
                              border: `1px solid ${BORDER}`,
                            }}
                          />
                        ) : isVideo ? (
                          <video
                            src={fullUrl}
                            style={{
                              width: 42,
                              height: 42,
                              borderRadius: "6px",
                            }}
                            controls
                          />
                        ) : (
                          <Box
                            onClick={() => isPdf && handleClickforpdf(fileUrl)}
                            sx={{
                              width: 42,
                              height: 42,
                              background: "#fff",
                              border: `1px solid ${BORDER}`,
                              borderRadius: "6px",
                              cursor: isPdf ? "pointer" : "default",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <img
                              src={fileIcon}
                              alt="file"
                              style={{ width: 22, height: 22 }}
                            />
                          </Box>
                        )}
                      </Box>
                    );
                  })()}
              </Paper>
            );
          })}
        </Box>
      )}

      {/* 🔹 Answer Rationale (reason_for_answer.explanation) */}
      {question?.correct_answers?.some(
        (ans) => ans?.reason_for_answer?.explanation,
      ) && (
        <Paper
          elevation={0}
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: "10px",
            background: "#eef6ff",
            border: "1px solid #cfe3ff",
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#1a6fd1",
              letterSpacing: "0.4px",
              mb: 0.5,
            }}
          >
            ANSWER RATIONALE
          </Typography>
          {question.correct_answers.map((ans, i) =>
            ans?.reason_for_answer?.explanation ? (
              <Typography
                key={i}
                sx={{
                  fontSize: "13px",
                  color: TEXT_PRIMARY,
                  lineHeight: 1.5,
                }}
              >
                {ans.reason_for_answer.explanation}
              </Typography>
            ) : null,
          )}
        </Paper>
      )}

      {mideaviewer && (
        <PdfViewer
          setMideaviewer={setMideaviewer}
          attachment={responseAttach}
        />
      )}

      {zoomedImage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <button
            onClick={() => setZoomedImage(null)}
            style={{
              position: "absolute",
              top: "20px",
              right: "30px",
              background: "transparent",
              border: "none",
              fontSize: "2rem",
              color: "white",
              cursor: "pointer",
              zIndex: 10000,
            }}
            aria-label="Close"
          >
            &times;
          </button>
          <img
            src={zoomedImage}
            alt="Zoomed"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              borderRadius: "8px",
              boxShadow: "0 0 10px #000",
            }}
          />
        </div>
      )}
    </Paper>
  );
};

export default PreMcq;
